import { $ } from 'rune-ts';
import { ResultQueriedByCursor } from '@services/MarppleShop/features/Funding';
import { isString } from '@fxts/core';
import { CSSSize } from '../../css-unit-parser.ts';

type ScrollLoadingCleanup = () => void | Promise<void>;
type ScrollLoadingReturn = void | Promise<void> | ScrollLoadingCleanup;
export type ScrollLoadingCallback = () => ScrollLoadingReturn;

const isPromise = <T>(value: any): value is Promise<T> => Boolean(value) && typeof value?.then === 'function';

export interface ScrollConfig<D, C> {
  container: HTMLElement | string;
  loader: (cursor: C) => Promise<ResultQueriedByCursor<D[], C>>;
  cursor: C;
  items?: D[];
  hasMore?: boolean;
  threshold?: number;
  thresholdPercentage?: number;
  debounce?: number;

  /**
   * 가상 스크롤 활성화
   */
  virtual: boolean;

  /**
   * 콜백
   */
  onRender?: (result: D[]) => Element[];
  loading?: ScrollLoadingCallback;
  onError?: (error: Error) => void | Promise<void>;
  onEnd?: () => void | Promise<void>;
}

export interface VirtualScrollConfig<D, C> extends ScrollConfig<D, C> {
  containerHeight: CSSSize;
  itemHeight: CSSSize | ((item: D) => CSSSize);
  gap?: CSSSize;
  overscan?: number;

  /**
   *
   */
  responsive?: boolean;

  /**
   * css 클래스
   */
  containerKlass?: string;
  itemKlass?: string;
}

export abstract class ScrollManager<D, C> {
  protected container: HTMLElement;
  protected cursor: C;
  protected hasMore: boolean;
  protected scroll = 0;
  protected initialized: boolean;
  protected readonly items = new Set<D[]>();
  protected readonly loader: (cursor: C) => Promise<ResultQueriedByCursor<D[], C>>;

  protected constructor(protected config: ScrollConfig<D, C>) {
    const { cursor, loader, items, hasMore = true } = this.config;
    this.initialized = true;
    this.cursor = cursor;
    this.hasMore = hasMore;
    this.container = this.createContainer();
    this.loader = loader;

    if (items) {
      this.items.add(items);
    }
  }

  get isInitialized() {
    return !(this.items.size === 0 && this.hasMore);
  }

  protected abstract initialize(): void;
  protected abstract render(data: D[]): void;
  protected createContainer() {
    const { container } = this.config;

    try {
      if (isString(container)) {
        const $container = $(container);
        return $container.element();
      }

      return container;
    } catch (_) {
      const error = new Error('InfiniteScrollManager createContainer: Container Element not found');
      this.config.onError?.(error);
      throw error;
    }
  }

  protected calculateThreshold(): number {
    if (this.config.thresholdPercentage !== undefined) {
      return Math.max(0, Math.min(1, this.config.thresholdPercentage));
    }
    return 0.1;
  }

  protected createScrollEvent() {
    let scrollTimeout: number;
    const saveScrollPosition = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => {
        this.scroll = this.container.scrollTop;
      }, 100);
    };

    this.container.addEventListener('scroll', saveScrollPosition, {
      passive: true,
    });
  }

  protected async load() {
    const promise = this.config.loading?.();

    const cleanup = isPromise(promise) ? await promise : promise;

    try {
      const { data, next_cursor, has_more } = await this.loader(this.cursor);

      this.cursor = next_cursor;
      this.hasMore = has_more;

      this.items.add(data);
      this.render(data);
    } catch (error: unknown) {
      if (error instanceof Error) this.config.onError?.(error);
    } finally {
      if (typeof cleanup === 'function') {
        isPromise(cleanup) ? await cleanup() : cleanup();
      }
    }
  }

  public abstract on(): this;
  public abstract off(): this;
  public abstract reset(): this;
  public abstract destroy(): this;
  public getItems() {
    return Array.from(this.items);
  }
}
